/**
 * Resolves a B2 signed-URL for chat images, refreshing on demand and caching
 * (in-memory + localStorage) so reloads don't always force a re-sign.
 *
 * Signed URLs from media.slumbr.xyz currently expire after 7 days
 * (URL_TTL_SECONDS in services/functions/media/download-url.ts). Anywhere
 * we render an image whose source is one of these URLs, the URL stored in
 * the message can still be expired (long-archived messages, clock skew,
 * server-side rotation). This module gives us a hook that:
 *   1. checks an in-memory + localStorage cache, returns a fresh URL if found
 *   2. otherwise calls /api/media/download-url to mint a new signed URL
 *   3. caches the result with its real expiresAt so future reads are cheap
 *
 * For older messages without an explicit objectKey we recover it from the
 * worker URL's pathname.
 *
 * `primeSignedUrl` lets callers warm the cache for an image they're about
 * to show (e.g. the lightbox prefetching neighbor images). The hook then
 * reads from the same cache on mount with no extra request.
 */

import { useEffect, useState } from "react";
import { getMediaDownloadUrl, useUserStore } from "@slumbr/core";

interface CachedUrl {
  url: string;
  /** ms-since-epoch when the URL stops being valid */
  expiresAt: number;
}

const SIGNED_URL_CACHE_KEY = "slumbr:messenger:signedUrlCache";
const REFRESH_BUFFER_MS = 60_000;
const scopedKey = (objectKey: string, conversationId: string) => JSON.stringify([useUserStore.getState().user?.id ?? "", conversationId, objectKey]);

function loadSignedUrlCache(): Map<string, CachedUrl> {
  if (typeof window === "undefined") return new Map();
  try {
    const raw = localStorage.getItem(SIGNED_URL_CACHE_KEY);
    if (!raw) return new Map();
    const parsed = JSON.parse(raw) as Record<string, CachedUrl>;
    const now = Date.now();
    const live = new Map<string, CachedUrl>();
    for (const [key, value] of Object.entries(parsed)) {
      if (value.expiresAt > now + REFRESH_BUFFER_MS) {
        live.set(key, value);
      }
    }
    return live;
  } catch {
    return new Map();
  }
}

const signedUrlCache: Map<string, CachedUrl> = loadSignedUrlCache();

let cachePersistTimer: ReturnType<typeof setTimeout> | null = null;
function scheduleCachePersist() {
  if (typeof window === "undefined") return;
  if (cachePersistTimer) clearTimeout(cachePersistTimer);
  cachePersistTimer = setTimeout(() => {
    try {
      const obj: Record<string, CachedUrl> = {};
      for (const [key, value] of signedUrlCache.entries()) obj[key] = value;
      localStorage.setItem(SIGNED_URL_CACHE_KEY, JSON.stringify(obj));
    } catch {
      // localStorage full or disabled — silently drop
    }
  }, 250);
}

/**
 * Extract the B2 objectKey from a media-worker URL when it isn't stored on
 * the message itself. Older messages (sent before objectKey was added to the
 * persisted shape) only have the signed URL — we can recover the key from
 * its pathname so the refresh hook still works for them.
 */
export function deriveObjectKeyFromUrl(url: string | undefined): string | undefined {
  if (!url) return undefined;
  try {
    const parsed = new URL(url);
    if (parsed.hostname !== "media.slumbr.xyz") return undefined;
    const decoded = decodeURIComponent(parsed.pathname.replace(/^\//, ""));
    if (decoded.startsWith("chat/") || decoded.startsWith("avatars/")) {
      return decoded;
    }
  } catch {
    // not a URL
  }
  return undefined;
}

/**
 * Evict a cached signed URL so the next consumer of `useSignedImageUrl`
 * fetches a fresh one. Call this from an img `onError` handler — combined
 * with a parent-side retry-nonce, the user transparently re-renders with
 * a working URL instead of seeing the broken-image fallback.
 */
export function evictSignedUrl(objectKey: string | undefined) {
  if (!objectKey) return;
  for (const key of signedUrlCache.keys()) {
    if (key === objectKey || key.endsWith(`,${JSON.stringify(objectKey)}]`)) signedUrlCache.delete(key);
  }
  scheduleCachePersist();
}

const inFlightPrimes = new Map<string, Promise<string | undefined>>();

/**
 * Warm the signed-URL cache for an image we are about to render. Returns
 * the resolved URL (or undefined on failure). Concurrent calls for the
 * same key dedupe to a single network request.
 *
 * Pair with `new Image().src = url` to also warm the browser HTTP cache so
 * the eventual `<img>` mount is decode-only.
 */
export function primeSignedUrl(
  objectKey: string | undefined,
  conversationId: string
): Promise<string | undefined> {
  if (!objectKey) return Promise.resolve(undefined);
  const key = scopedKey(objectKey, conversationId);
  const cached = signedUrlCache.get(key);
  if (cached && cached.expiresAt > Date.now() + REFRESH_BUFFER_MS) {
    return Promise.resolve(cached.url);
  }
  const inflight = inFlightPrimes.get(key);
  if (inflight) return inflight;
  const promise = getMediaDownloadUrl(objectKey, conversationId)
    .then(({ url, expiresAt }) => {
      if (key !== scopedKey(objectKey, conversationId)) return undefined;
      if (signedUrlCache.size >= 500) signedUrlCache.delete(signedUrlCache.keys().next().value!);
      signedUrlCache.set(key, {
        url,
        expiresAt: new Date(expiresAt).getTime(),
      });
      scheduleCachePersist();
      return url;
    })
    .catch(() => undefined)
    .finally(() => {
      if (inFlightPrimes.get(key) === promise) inFlightPrimes.delete(key);
    });
  inFlightPrimes.set(key, promise);
  return promise;
}

export function useSignedImageUrl(
  objectKey: string | undefined,
  conversationId: string,
  fallback: string,
  /** Incrementing this triggers a re-fetch even if effectiveKey is stable —
   * use it from img onError handlers to recover from broken signed URLs. */
  retryNonce: number = 0
): string {
  const effectiveKey = objectKey ?? deriveObjectKeyFromUrl(fallback);
  const key = effectiveKey ? scopedKey(effectiveKey, conversationId) : fallback;

  const initial = (() => {
    if (!effectiveKey) return fallback;
    const cached = signedUrlCache.get(key);
    if (cached && cached.expiresAt > Date.now() + REFRESH_BUFFER_MS) {
      return cached.url;
    }
    return fallback;
  })();

  const [resolved, setResolved] = useState({ key, url: initial });

  useEffect(() => {
    // No object key (e.g. GIF served from a public CDN) — just mirror the
    // fallback. Without this branch the hook would keep the previous
    // image's `resolved` state when the consumer navigates from a B2 image
    // to a Klipy GIF in the lightbox, leaving the wrong URL on screen.
    if (!effectiveKey) {
      setResolved({ key, url: fallback });
      return;
    }

    const cached = signedUrlCache.get(key);
    if (cached && cached.expiresAt > Date.now() + REFRESH_BUFFER_MS) {
      setResolved({ key, url: cached.url });
      return;
    }

    let cancelled = false;
    // Route through primeSignedUrl so concurrent consumers of the same key
    // (e.g. lightbox thumb + main view + bubble) share one network request.
    primeSignedUrl(effectiveKey, conversationId).then((url) => {
      if (cancelled || !url) return;
      setResolved({ key, url });
    });

    return () => {
      cancelled = true;
    };
  }, [key, effectiveKey, conversationId, fallback, retryNonce]);

  return resolved.key === key ? resolved.url : initial;
}
